import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Lock, Play, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PageHeader, Panel } from "@/components/layout/AppShell";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { useRx } from "@/context/AuthContext";
import { fmt } from "@/lib/format";

const DEFAULTS = { symbol: "NIFTY", ema_fast: 9, ema_slow: 21, rsi_period: 14, rsi_long_min: 52, rsi_short_max: 48, volume_mult: 0.8, sl_atr: 1.5, target_atr: 3, trail_atr: 0, direction: "both" };
const SLIDERS = [
  ["ema_fast", "Fast EMA", 2, 50, 1], ["ema_slow", "Slow EMA", 5, 200, 1], ["rsi_period", "RSI period", 2, 50, 1],
  ["rsi_long_min", "RSI min for longs", 0, 100, 1], ["rsi_short_max", "RSI max for shorts", 0, 100, 1],
  ["volume_mult", "Volume filter (x avg)", 0, 5, 0.1], ["sl_atr", "Stop loss (x ATR)", 0.5, 10, 0.1],
  ["target_atr", "Target (x ATR)", 0.5, 20, 0.1], ["trail_atr", "Trailing (x ATR, 0 = off)", 0, 10, 0.1],
];

function Locked() {
  return (
    <div className="relative overflow-hidden rx-card p-10 lg:p-16 text-center" data-testid="pro-locked">
      <img src="https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?crop=entropy&cs=srgb&fm=jpg&q=60&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
      <div className="relative">
        <div className="mx-auto h-14 w-14 rounded-2xl border border-[#FFCC00]/40 bg-[#FFCC00]/10 grid place-items-center"><Lock className="text-[#FFCC00]" /></div>
        <h2 className="font-display text-2xl lg:text-3xl font-bold mt-6">Pro Trader Desk is a Premium feature</h2>
        <p className="text-gray-400 mt-3 max-w-lg mx-auto text-sm">Build your own rule-based strategies, tune every parameter, backtest on live-feed history and save setups.</p>
        <Link to="/app/pricing" data-testid="pro-upgrade-cta" className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#FFCC00] text-black font-bold px-6 py-2.5 hover:brightness-110 transition-[filter]"><Crown size={16} /> Upgrade to Pro</Link>
      </div>
    </div>
  );
}

function Results({ r }) {
  if (!r) return <Panel title="Results"><div className="text-sm text-gray-500 py-16 text-center">Tune parameters and press Run backtest.</div></Panel>;
  const s = r.stats;
  const tiles = [["Trades", s.trades], ["Win rate", `${s.win_rate}%`], ["Net points", fmt(s.net_pts)], ["Profit factor", s.profit_factor ?? "—"], ["Max drawdown", fmt(s.max_drawdown)], ["Live signal", r.live_signal]];
  return (
    <div className="space-y-4" data-testid="pro-results">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {tiles.map(([k, v]) => (
          <div key={k} className="rx-card p-3"><div className="rx-label">{k}</div>
            <div className="font-data text-lg font-bold mt-1" data-testid={`pro-stat-${k.toLowerCase().replace(/ /g, "-")}`}>{v}</div></div>
        ))}
      </div>
      <Panel title="Equity curve (points)">
        <div className="h-52"><ResponsiveContainer><AreaChart data={r.equity}>
          <defs><linearGradient id="pe" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#007AFF" stopOpacity={0.4} /><stop offset="100%" stopColor="#007AFF" stopOpacity={0} /></linearGradient></defs>
          <XAxis dataKey="time" hide /><YAxis stroke="#4A5568" fontSize={11} width={48} />
          <Tooltip contentStyle={{ background: "#11151C", border: "1px solid rgba(255,255,255,.1)", fontSize: 12 }} labelFormatter={(t) => new Date(t * 1000).toLocaleTimeString("en-IN")} />
          <Area type="monotone" dataKey="value" stroke="#007AFF" fill="url(#pe)" strokeWidth={2} />
        </AreaChart></ResponsiveContainer></div>
      </Panel>
      <Panel title="Trades">
        <div className="max-h-64 overflow-y-auto rx-scroll">
          <table className="w-full text-xs font-data"><tbody>
            {r.trades.map((t, i) => (
              <tr key={i} className="border-b border-white/5">
                <td className="py-1.5 text-gray-500">{new Date(t.time * 1000).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</td>
                <td className={t.direction === "LONG" ? "text-[#00FF66]" : "text-[#FF3366]"}>{t.direction}</td>
                <td>{fmt(t.entry)} → {fmt(t.exit)}</td>
                <td className={`text-right ${t.pnl_pts >= 0 ? "text-[#00FF66]" : "text-[#FF3366]"}`}>{fmt(t.pnl_pts)}</td>
              </tr>
            ))}
            {!r.trades.length && <tr><td className="py-4 text-gray-500">No trades with these rules.</td></tr>}
          </tbody></table>
        </div>
      </Panel>
    </div>
  );
}

export default function ProDesk() {
  const { isPro, me, api } = useRx();
  const [p, setP] = useState(DEFAULTS);
  const [res, setRes] = useState(null);
  const [symbols, setSymbols] = useState([]);
  const [saved, setSaved] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isPro) return;
    api("get", "/pro/symbols").then((d) => setSymbols(d.symbols)).catch(() => {});
    api("get", "/pro/strategies").then((d) => setSaved(d.items)).catch(() => {});
  }, [isPro, api]);

  if (!me) return <div className="h-64 rx-card animate-pulse" />;
  if (!isPro) return (<><PageHeader eyebrow="Premium" title="Pro Trader Desk" /><Locked /></>);

  const run = async (fn) => {
    setBusy(true);
    try { setRes(await fn()); } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Invalid parameters"); }
    setBusy(false);
  };
  const save = async () => {
    try { const d = await api("post", "/pro/strategies", { name: name || `${p.symbol} EMA ${p.ema_fast}/${p.ema_slow}`, params: p }); setSaved([d, ...saved]); setName(""); toast.success("Strategy saved"); }
    catch (e) { toast.error("Could not save"); }
  };
  const del = async (id) => { await api("delete", `/pro/strategies/${id}`); setSaved(saved.filter((s) => s.id !== id)); };

  return (
    <div data-testid="pro-desk-page">
      <PageHeader eyebrow="Premium · Your own logic" title="Pro Trader Desk" sub="Build a rule-based strategy (EMA crossover + RSI + volume filter + ATR exits), backtest it on the live feed history and save it." />
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <div className="xl:col-span-2 space-y-4">
          <Panel title="Strategy builder" testId="pro-builder">
            <div className="grid grid-cols-2 gap-3 mb-5">
              <div><div className="text-xs text-gray-400 mb-1.5">Instrument</div>
                <select value={p.symbol} onChange={(e) => setP({ ...p, symbol: e.target.value })} data-testid="pro-symbol-select"
                  className="w-full h-10 rounded-md bg-black/40 border border-white/10 px-3 text-sm font-data">
                  {symbols.map((s) => <option key={s.symbol} value={s.symbol}>{s.symbol}</option>)}
                </select></div>
              <div><div className="text-xs text-gray-400 mb-1.5">Direction</div>
                <select value={p.direction} onChange={(e) => setP({ ...p, direction: e.target.value })} data-testid="pro-direction-select"
                  className="w-full h-10 rounded-md bg-black/40 border border-white/10 px-3 text-sm">
                  <option value="both">Long & Short</option><option value="long">Long only</option><option value="short">Short only</option>
                </select></div>
            </div>
            <div className="space-y-5">
              {SLIDERS.map(([k, l, min, max, step]) => (
                <div key={k} data-testid={`pro-param-${k}`}>
                  <div className="flex justify-between text-xs mb-2"><span className="text-gray-400">{l}</span><span className="font-data text-[#00FF66]">{p[k]}</span></div>
                  <Slider min={min} max={max} step={step} value={[p[k]]} onValueChange={([v]) => setP({ ...p, [k]: v })} />
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => run(() => api("post", "/pro/run", p))} disabled={busy} data-testid="pro-run-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#00FF66] text-black font-bold py-2.5 hover:brightness-110 transition-[filter] disabled:opacity-50"><Play size={16} /> {busy ? "Running…" : "Run backtest"}</button>
              <button onClick={() => setP(DEFAULTS)} data-testid="pro-reset-btn" className="rounded-full border border-white/15 px-4 text-sm hover:bg-white/5 transition-colors">Reset</button>
            </div>
            <div className="flex gap-2 mt-4">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Strategy name" data-testid="pro-strategy-name" className="bg-black/40 border-white/10" />
              <button onClick={save} data-testid="pro-save-btn" className="inline-flex items-center gap-2 rounded-full bg-[#007AFF] px-4 text-sm font-bold hover:bg-[#0056b3] transition-colors"><Save size={14} /> Save</button>
            </div>
          </Panel>
          <Panel title="Saved strategies" testId="pro-saved">
            {!saved.length && <div className="text-sm text-gray-500">None yet.</div>}
            <div className="space-y-2">
              {saved.map((s) => (
                <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`saved-strategy-${s.id}`}>
                  <button className="flex-1 text-left text-sm truncate hover:text-[#00FF66]" onClick={() => setP(s.params)}>{s.name}<span className="text-xs text-gray-500 ml-2 font-data">{s.params.symbol}</span></button>
                  <button onClick={() => run(() => api("post", `/pro/strategies/${s.id}/run`))} className="p-1.5 text-gray-400 hover:text-[#00FF66]" aria-label="Run" data-testid={`run-saved-${s.id}`}><Play size={14} /></button>
                  <button onClick={() => del(s.id)} className="p-1.5 text-gray-400 hover:text-[#FF3366]" aria-label="Delete" data-testid={`delete-saved-${s.id}`}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </Panel>
        </div>
        <div className="xl:col-span-3"><Results r={res} /></div>
      </div>
    </div>
  );
}
